import { Box } from 'components/primitives/box';
import { FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import { Text } from 'components/primitives/typography';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import styled from 'styled-components';
import { renderAtomCheckButton } from '../fields/AtomCheckButton';
import AtomSlider from '../fields/AtomSlider';
import {
  activitiesAtom,
  activitiesManipulatorAtom,
  allFiltersActiveCountAtom,
  boatAmenitiesManipulatorAtom,
  boatCategoriesAtom,
  boatCategoriesManipulatorAtom,
  boatLengthAtom,
  boatMakesAtom,
  boatMakesManipulatorAtom,
  boatTypesAtom,
  boatTypesManipulatorAtom,
  durationHoursManipulatorAtom,
  priceAtom,
} from '../filterStore';
import CollapsibleFilter from './CollapsibleFilter';
import ExclusiveFilterSwitch, { ExclusiveFilterLabel } from './ExclusiveFilterSwitch';
import PassengersFilterContent from './PassengersFilterContent';
import { DurationFilterContentInOtherFilters } from './DurationFilterContent';
import CaptainFilterContent from './CaptainFilterContent';
import InstantBookFilterSwitch, { InstantBookFilterLabel } from './InstantBookFilterSwitch';
import BoatAmenitiesContent from './BoatAmenitiesContent';

const getOtherFilters = () => [
  {
    label: 'Filters',
    isHeading: true,
  },
  /* Removing SortingFilterButton based SRCH-154 */
  // {
  //   label: 'Sort by',
  //   headerExtraContent: <SortingFilterButton mobileVariant />,
  // },

  // ...(smallScreen
  //   ? [
  {
    label: 'Duration (hours)',
    isCollapsible: true,
    content: <DurationFilterContentInOtherFilters />,
    openByDefault: true,
  },
  {
    label: 'Number of passengers',
    isCollapsible: true,
    content: <PassengersFilterContent height="70px" />,
    openByDefault: true,
  },
  //   ]
  // : []),
  {
    isCollapsible: true,
    label: 'Captain options',
    content: <CaptainFilterContent />,
    openByDefault: true,
  },
  {
    label: 'Price per hour',
    hideLabel: true,
    content: <AtomSlider atom={priceAtom} />,
    openByDefault: true,
  },
  {
    label: 'Activities',
    content: <CheckButtonsContent atom={activitiesAtom} data-testid="search-other-filters-activities-box" />,
    isCollapsible: true,
    // headerExtraContent: <SelectedValuesCounter checkedValuesAtom={activitiesManipulatorAtom} />,
    openByDefault: true,
  },
  {
    label: 'Boat amenities',
    isCollapsible: true,
    openByDefault: true,
    content: <BoatAmenitiesContent />,
  },
  {
    label: 'Boat length',
    hideLabel: true,
    content: <AtomSlider atom={boatLengthAtom} />,
  },
  {
    label: 'Boat type',
    content: <CheckButtonsContent atom={boatTypesAtom} data-testid="search-other-filters-boat-type-box" />,
    isCollapsible: true,
    // headerExtraContent: <SelectedValuesCounter checkedValuesAtom={boatTypesManipulatorAtom} />,
  },
  {
    label: 'Boat category',
    content: <CheckButtonsContent atom={boatCategoriesAtom} data-testid="search-other-filters-boat-category-box" />,
    isCollapsible: true,
    // headerExtraContent: <SelectedValuesCounter checkedValuesAtom={boatCategoriesManipulatorAtom} />,
  },
  {
    label: 'Boat manufacturer',
    content: <CheckButtonsContent atom={boatMakesAtom} data-testid="search-other-filters-boat-manufacturer-box" />,
    isCollapsible: true,
    // headerExtraContent: <SelectedValuesCounter checkedValuesAtom={boatMakesManipulatorAtom} />,
  },
  {
    label: <InstantBookFilterLabel />,
    headerExtraContent: <InstantBookFilterSwitch />,
  },
  {
    label: <ExclusiveFilterLabel />,
    headerExtraContent: <ExclusiveFilterSwitch data-testid="search-other-filters-boatsetter-exclusive-toggle" />,
  },
];

export default function OtherFiltersContent() {
  const otherFilters = useMemo(() => getOtherFilters(), []);

  return (
    <>
      {otherFilters.map(
        ({ content, label, hideLabel, headerExtraContent, isCollapsible, isHeading, openByDefault }) => (
          <FilterRow
            openByDefault={openByDefault}
            key={label.toString()}
            label={!hideLabel && label}
            headerExtraContent={headerExtraContent}
            isCollapsible={isCollapsible}
            isHeading={isHeading}
          >
            {content}
          </FilterRow>
        )
      )}
    </>
  );
}

const CheckButtonsContent = styled(({ atom, ...others }) => {
  const options = useAtomValue<[]>(atom);
  return <div {...others}>{options.map(renderAtomCheckButton)}</div>;
})`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 14px;
`;

const FilterRow = styled(
  ({ label, headerExtraContent, children, isCollapsible, isHeading, openByDefault, ...other }) => {
    if (isCollapsible) {
      return (
        <Box padding="24px 0 32px" {...other}>
          <CollapsibleFilter
            openByDefault={openByDefault}
            headerContent={
              <FlexAlignCenterSpaceBetween flex="1 1">
                <Text fWeight="600" fSize="16px">
                  {label}
                </Text>
                {headerExtraContent}
              </FlexAlignCenterSpaceBetween>
            }
          >
            {children}
          </CollapsibleFilter>
        </Box>
      );
    }
    return (
      <Box padding={isHeading ? '24px 0 4px' : '24px 0 32px'} {...other}>
        {label && (
          <FlexAlignCenterSpaceBetween mb={children && '32px'}>
            <Text fWeight={isHeading ? '700' : '600'} fSize={isHeading ? '18px' : '16px'}>
              {label}
            </Text>
            {headerExtraContent}
          </FlexAlignCenterSpaceBetween>
        )}
        {children}
      </Box>
    );
  }
)`
  &:not(:last-child) {
    border-bottom: 1px solid #dbdfe5;
  }
  ${({ isHeading }) => isHeading && 'border-bottom: none !important'};
`;

export const OtherFiltersLabel = () => {
  const allFiltersActiveCount = useAtomValue(allFiltersActiveCountAtom);
  const arrayFiltersCount =
    useAtomValue(boatTypesManipulatorAtom).length +
    useAtomValue(boatCategoriesManipulatorAtom).length +
    useAtomValue(boatMakesManipulatorAtom).length +
    useAtomValue(activitiesManipulatorAtom).length +
    useAtomValue(durationHoursManipulatorAtom).length +
    useAtomValue(boatAmenitiesManipulatorAtom).length;
  const allFiltersCount = arrayFiltersCount + allFiltersActiveCount;
  return (
    <>
      <FilterIcon /> Filters{allFiltersCount !== 0 && ` (${allFiltersCount})`}
    </>
  );
};

const FilterIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path
      d="M13 2.66668H8.33333M5.66667 2.66668H1M13 8.00001H7M4.33333 8.00001H1M13 13.3333H9.66667M7 13.3333H1M8.33333 1.33334V4.00001M4.33333 6.66668V9.33334M9.66667 12V14.6667"
      stroke="#17233C"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
